import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase: VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ===== TYPES =====
export interface Post {
  id: string
  title: string
  slug: string
  cover_image_url?: string
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  published_at?: string
  created_at: string
  updated_at: string
  author_id: string
}

export interface Tag {
  id: string
  name: string
  slug: string
  description?: string
  created_at: string
}

// ===== BLOG API =====
export const blogApi = {
  // Obtener posts publicados con autor y tags
  async getPublishedPosts() {
    // Primero obtenemos los posts
    const { data: posts, error } = await supabase
      .from('posts')
      .select('*')
      .eq('status', 'PUBLISHED')
      .order('published_at', { ascending: false })

    if (error) {
      console.error('Error obteniendo posts:', error)
      throw error
    }

    if (!posts || posts.length === 0) {
      return []
    }

    // Luego obtenemos los autores y tags por separado
    const postsWithAuthorsAndTags = await Promise.all(
      posts.map(async (post) => {
        // Obtener información del autor
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', post.author_id)
          .single()

        // Obtener tags del post
        const { data: postTags } = await supabase
          .from('post_tags')
          .select(`
            tags (
              name
            )
          `)
          .eq('post_id', post.id)

        const tags = postTags?.map(pt => ({ name: pt.tags?.name })).filter(t => t.name) || []

        return {
          ...post,
          author_name: profile?.full_name || 'Autor Desconocido',
          post_tags: tags
        }
      })
    )

    return postsWithAuthorsAndTags
  },

  // Obtener un post por slug
  async getPostBySlug(slug: string) {
    const { data: post, error } = await supabase
      .from('posts')
      .select('*')
      .eq('slug', slug)
      .in('status', ['PUBLISHED', 'DRAFT'])
      .single()

    if (error) {
      console.error('Error obteniendo post:', error)
      throw error
    }

    // Obtener información del autor
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', post.author_id)
      .single()

    // Obtener las secciones de contenido
    const { data: sections } = await supabase
      .from('content_sections')
      .select('*')
      .eq('post_id', post.id)
      .order('order')

    // Obtener tags del post
    const { data: postTags } = await supabase
      .from('post_tags')
      .select(`
        tags (
          name
        )
      `)
      .eq('post_id', post.id)

    const tags = postTags?.map(pt => ({ name: pt.tags?.name })).filter(t => t.name) || []

    return {
      ...post,
      author_name: profile?.full_name || 'Autor Desconocido',
      content_sections: sections || [],
      post_tags: tags
    }
  },

  // Obtener todos los tags
  async getTags() {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .order('name')

    if (error) {
      console.error('Error obteniendo tags:', error)
      throw error
    }

    return data || []
  },

  // Obtener URL de imagen del storage
  getImageUrl(path: string) {
    return supabase.storage.from('blog-images').getPublicUrl(path).data.publicUrl
  },

  // Subir imagen al storage
  async uploadImage(file: File, path: string) {
    const { data, error } = await supabase.storage
      .from('fourinbucket')
      .upload(crypto.randomUUID() + '_' + path, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) {
      console.error('Error subiendo imagen:', error)
      throw error
    }

    return data
  },

  // Función para subir imagen a Supabase
  async uploadImageToSupabase(file: File): Promise<string> {
    const fileName = `${crypto.randomUUID()}_${file.name}`
    const { data, error } = await supabase.storage
      .from('fourinbucket')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) throw error

    // Obtener URL pública
    const { data: { publicUrl } } = supabase.storage
      .from('fourinbucket')
      .getPublicUrl(fileName)

    return publicUrl
  },

  // Obtener todos los posts para admin (incluyendo borradores)
  async getAllPostsForAdmin() {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        id,
        title,
        slug,
        status,
        created_at,
        updated_at,
        published_at
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error obteniendo posts para admin:', error)
      throw error
    }

    // Los datos ya vienen con el formato correcto
    return data || []
  },

  // Test de conexión
  async testStorage() {
    try {
      const { data, error } = await supabase.storage
        .from('blog-images')
        .list('', { limit: 1 })

      if (error) {
        console.warn('Storage test failed:', error)
        return { success: false, error }
      }

      return { success: true, data }
    } catch (error) {
      console.warn('Storage test failed:', error)
      return { success: false, error }
    }
  },

  // Obtener un post para edición (incluye contenido y relaciones)
  async getPostForEditing(postId: string) {
    try {
      // Obtener el post básico
      const { data: post, error: postError } = await supabase
        .from('posts')
        .select('*')
        .eq('id', postId)
        .single()

      if (postError) throw postError

      // Obtener las secciones de contenido
      const { data: sections, error: sectionsError } = await supabase
        .from('content_sections')
        .select('*')
        .eq('post_id', postId)
        .order('order')

      if (sectionsError) throw sectionsError

      // Obtener tags del post
      const { data: postTags } = await supabase
        .from('post_tags')
        .select(`
          tags (
            name
          )
        `)
        .eq('post_id', postId)

      const tags = postTags?.map(pt => pt.tags?.name).filter(name => name) || []

      return {
        ...post,
        content_sections: sections || [],
        tags: tags
      }
    } catch (error) {
      console.error('Error obteniendo post para edición:', error)
      throw error
    }
  },

  // Crear o actualizar un post
  async upsertPost(payload: {
    id?: string | null
    title: string
    slug: string
    status: 'DRAFT' | 'PUBLISHED'
    cover_image_url?: string
    author_id?: string
    content_sections: Array<{
      id?: string
      order: number
      type: 'HEADING' | 'PARAGRAPH' | 'IMAGE'
      content: any
    }>
    tags: number[]
  }) {
    try {
      let postId = payload.id

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Usuario no autenticado')

      // Si es un post nuevo, crearlo primero
      if (!postId) {
        // Obtener el usuario actual


        const { data: newPost, error: postError } = await supabase
          .from('posts')
          .insert({
            title: payload.title,
            slug: payload.slug,
            status: payload.status,
            cover_image_url: payload.cover_image_url || null,
            author_id: payload.author_id || user.id,
            published_at: payload.status === 'PUBLISHED' ? new Date().toISOString() : null
          })
          .select()
          .single()

        if (postError) throw postError
        postId = newPost.id
      } else {
        // Actualizar post existente
        const { error: updateError } = await supabase
          .from('posts')
          .update({
            title: payload.title,
            slug: payload.slug,
            status: payload.status,
            cover_image_url: payload.cover_image_url || null,
            author_id: payload.author_id || user.id,
            published_at: payload.status === 'PUBLISHED' ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', postId)

        if (updateError) throw updateError
      }

      // Eliminar secciones existentes y recrearlas
      await supabase
        .from('content_sections')
        .delete()
        .eq('post_id', postId)

      if (payload.content_sections.length > 0) {
        const sectionsToInsert = payload.content_sections.map(section => ({
          post_id: postId,
          order: section.order,
          type: section.type,
          content: section.content
        }))

        const { error: sectionsError } = await supabase
          .from('content_sections')
          .insert(sectionsToInsert)

        if (sectionsError) throw sectionsError
      }

      // Manejar tags - LÓGICA ACTUALIZADA
      if (postId) {
        // 1. Obtener tags actuales del post
        const { data: currentTags } = await supabase
          .from('post_tags')
          .select('tag_id')
          .eq('post_id', postId)

        const currentTagIds = currentTags?.map(t => t.tag_id) || []

        // 2. Determinar qué tags añadir y cuáles eliminar
        const tagsToAdd = payload.tags.filter(tagId => !currentTagIds.includes(tagId))
        const tagsToRemove = currentTagIds.filter(tagId => !payload.tags.includes(tagId))

        // 3. Eliminar tags que ya no están seleccionados
        if (tagsToRemove.length > 0) {
          const { data, error, status } = await supabase
            .from('post_tags')
            .delete()
            .eq('post_id', postId)
            .in('tag_id', tagsToRemove)

        }

        // 4. Insertar solo los nuevos tags
        if (tagsToAdd.length > 0) {
          const newTagRelations = tagsToAdd.map(tagId => ({
            post_id: postId,
            tag_id: tagId
          }))

          const { error: tagsError } = await supabase
            .from('post_tags')
            .insert(newTagRelations)

          if (tagsError) throw tagsError
        }
      }

      return { id: postId }
    } catch (error) {
      console.error('Error guardando post:', error)
      throw error
    }
  },

  // Crear o actualizar un tag
  async upsertTag(payload: {
    id?: number,
    name: string
  }): Promise<{ id: number; name: string }> {
    try {
      if (payload.id) {
        // Actualizar tag existente
        const { data, error } = await supabase
          .from('tags')
          .update({ name: payload.name })
          .eq('id', payload.id)
          .select()
          .single()

        if (error) throw error
        return data
      } else {
        // Crear nuevo tag
        const { data, error } = await supabase
          .from('tags')
          .insert({ name: payload.name })
          .select()
          .single()

        if (error) throw error
        return data
      }
    } catch (error) {
      console.error('Error en upsertTag:', error)
      throw error
    }
  },
}

// ===== DEMO API =====

// Notifica al equipo (info@fourin.co + jesus.cabarca@fourin.co) vía Edge Function.
// Fire-and-forget: un fallo de email NUNCA debe romper el guardado de la solicitud.
export async function notifyDemoTeam(payload: {
  nombre?: string | null
  empresa?: string | null
  email: string
  telefono?: string | null
  whatsapp?: string | null
  team_size?: string | null
  mensaje?: string | null
}) {
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/send-demo-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      console.error('Error notificando demo al equipo:', await res.text())
    }
  } catch (error) {
    console.error('Error notificando demo al equipo:', error)
  }
}

// Función helper para enviar solicitudes de demo
export async function submitDemoRequest(formData: {
  nombre: string
  empresa: string
  email: string
  telefono?: string
  mensaje?: string
}) {
  const { data, error } = await supabase
    .from('demo_requests')
    .insert([
      {
        nombre: formData.nombre,
        empresa: formData.empresa,
        email: formData.email,
        telefono: formData.telefono || null,
        mensaje: formData.mensaje || null,
        status: 'pending'
      }
    ])
    .select()

  if (error) {
    console.error('Error guardando en Supabase:', error)
    throw new Error('Error guardando la solicitud: ' + error.message)
  }

  void notifyDemoTeam(formData)

  return data
}

// ===== CONTACTO =====
export async function submitContactMessage(payload: {
  first_name: string
  last_name: string
  company: string
  email: string
  phone: string
  message: string
}) {
  const { data, error } = await supabase
    .from('contact_messages')
    .insert([{ ...payload, status: 'pending' }])
    .select()

  if (error) {
    console.error('Error guardando mensaje de contacto:', error)
    throw new Error('Error enviando el mensaje: ' + error.message)
  }

  return data
}

// ===== DEMO 2 PASOS (FM-537) =====

// Paso 1: solo email + origen + UTM. Devuelve el token para completar el paso 2.
export async function createDemoRequestStep1(payload: {
  email: string
  source?: string
  utm_source?: string | null
  utm_campaign?: string | null
}) {
  const { data, error } = await supabase
    .from('demo_requests')
    .insert([
      {
        email: payload.email,
        source: payload.source || 'home',
        utm_source: payload.utm_source || null,
        utm_campaign: payload.utm_campaign || null,
        status: 'pending',
      },
    ])
    .select('id, token')
    .single()

  if (error) {
    console.error('Error creando solicitud de demo (paso 1):', error)
    throw new Error('Error guardando la solicitud: ' + error.message)
  }

  return data as { id: string | number; token: string }
}

// Paso 2: completa los datos. Localiza por token (no por id).
export async function updateDemoRequestStep2(
  token: string,
  payload: {
    nombre: string
    empresa: string
    whatsapp?: string
    team_size?: string
  },
) {
  const { data, error } = await supabase
    .from('demo_requests')
    .update({
      nombre: payload.nombre,
      empresa: payload.empresa,
      whatsapp: payload.whatsapp || null,
      team_size: payload.team_size || null,
    })
    .eq('token', token)
    .select()

  if (error) {
    console.error('Error completando solicitud de demo (paso 2):', error)
    throw new Error('Error completando la solicitud: ' + error.message)
  }

  const row = data?.[0] as { email?: string } | undefined
  if (row?.email) {
    void notifyDemoTeam({
      email: row.email,
      nombre: payload.nombre,
      empresa: payload.empresa,
      whatsapp: payload.whatsapp,
      team_size: payload.team_size,
    })
  }

  return data
}

// Función para obtener todas las solicitudes de demo (para admin)
export async function getDemoRequests() {
  const { data, error } = await supabase
    .from('demo_requests')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error obteniendo solicitudes de demo:', error)
    throw error
  }

  return data || []
}
